/**
 * Данные компании для страницы профиля нужно брать из ProfileStore
 * так как в CompanyCardStore данные компании не актуальны именно для Профиля
 *
 */

import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "stores";
import { Alert, Collapse, Flex, Spin } from "antd";

import { routes } from "constants/routes";
import { getStatusMessage, setDefaultBankDetails } from "services";
import { parseErrorMessage } from "utils";
import { useNotificationContext } from "providers";
import { BankAccountGrid, COMPANY_ROLE_ENUM, NOTIFICATION_TYPE_ENUM } from "interfaces";

import styles from "./bank.module.css";
import { getBankAccountCollapseItem } from "./BankAccountCollapseItem";
import { SAFE_DEAL } from "utils/envConstants";

export const BankDetailsProfile = observer(() => {
  const { user, bankAccountsStore, bankAccountsDetailStore, companyCard, profile } = useStores();
  const { openNotification } = useNotificationContext();
  const { bankAccounts } = bankAccountsStore;
  const [sortedBankAccounts, setSortedBankAccounts] = useState<BankAccountGrid[]>([]);

  const [loading, setLoading] = useState(false);
  const [openedBankAccountsId, setOpenedBankAccountsId] = useState<string[]>([]);

  const { isSeniorCustomer, isSeniorLP, isSystemAdmin } = user.roles;

  const isProfilePage = window.location.pathname.replace("/", "") === routes.profile;

  const isSafeDealMode =
    SAFE_DEAL &&
    profile.company.beneficiaryCreated &&
    sortedBankAccounts.length >= 2 &&
    profile.company.role === COMPANY_ROLE_ENUM.OPERATOR;

  const showDefaultAccChangeButton =
    isProfilePage && (isSeniorCustomer || isSeniorLP || isSystemAdmin) && !isSafeDealMode;

  const canArchiveBankAccount = (isSeniorCustomer || isSeniorLP || isSystemAdmin) && isProfilePage;

  const companyId = isProfilePage ? user.userInfo?.company_id : companyCard.card.id;

  useEffect(() => {
    if (companyId) {
      bankAccountsStore.fetchBankAccounts(companyId);
    }
  }, [companyId, bankAccountsStore]);

  useEffect(() => {
    setSortedBankAccounts(bankAccounts.sort((a, b) => (a.isDefault ? -1 : 1)));
  }, [bankAccounts]);

  const handleCollapseChange = async (keys: string[]) => {
    const newOpenedAccount = keys.find((key) => !openedBankAccountsId.includes(key));

    if (companyId && newOpenedAccount) {
      await bankAccountsDetailStore.fetchBankAccountDetails(companyId, newOpenedAccount);
    }

    setOpenedBankAccountsId(keys);
  };

  const changeDefaultBankAccount = async (index: string) => {
    if (!companyId) return;
    setLoading(true);

    await setDefaultBankDetails({ id: companyId, accountId: index })
      .then(() => {
        openNotification({
          message: "Реквизиты по умолчанию установлены",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });

        setTimeout(() => {
          bankAccountsStore.fetchBankAccounts(companyId);
        }, 1000);
      })
      .catch((e) => {
        openNotification({
          description: parseErrorMessage(e) ?? "Не удалось установить реквизиты по умолчанию",
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Spin spinning={bankAccountsStore.isPending}>
      {bankAccountsStore.fetchStatus === "success" && (
        <Flex vertical gap="small">
          {sortedBankAccounts.map((item) => (
            <div key={item.id}>
              <Collapse
                items={getBankAccountCollapseItem({
                  item,
                  showDefaultAccChangeButton,
                  loading,
                  canArchiveBankAccount,
                  bankAccountsLength: bankAccounts.length,
                  onSetDefault: changeDefaultBankAccount,
                  bankAccountsDetailStore,
                  isSafeDealMode,
                })}
                expandIconPosition="end"
                bordered={false}
                onChange={(e) => {
                  const keys = Array.isArray(e) ? e : [e];
                  handleCollapseChange(keys);
                }}
                activeKey={openedBankAccountsId.includes(item.id) ? [item.id] : []}
                className={`default-collapse bg-basic-200 ${styles.collapse}`}
              />
            </div>
          ))}
        </Flex>
      )}
      {bankAccountsStore.fetchStatus === "error" && bankAccountsStore.fetchError && (
        <Alert
          message={getStatusMessage(bankAccountsStore.fetchError.status)}
          description={parseErrorMessage(bankAccountsStore.fetchError)}
          type="error"
          showIcon
          className="margin-top-lg"
        />
      )}
    </Spin>
  );
});
